// apps/web/src/lib/apiClient.ts
//
// Central API client for the web app. Wraps fetch with the security +
// UX behavior specified in reference/phase-1-5-FrontendPhase.md §6:
//
//   - always sends `credentials: 'include'` (auth cookies)
//   - attaches a CSRF header on every state-changing request
//     (the backend checks for it on PUT/POST/DELETE/PATCH)
//   - parses the standard `{ error: { code, message, requestId } }` envelope
//   - on 401, attempts ONE silent refresh; if that fails, dispatches
//     a `session-expired` window event (not a hard redirect — that
//     would silently discard unsaved form state per §9.3)
//   - race-safe refresh: concurrent 401s share a single in-flight
//     refresh promise (otherwise multiple tabs racing would each
//     trigger their own rotation and revoke the family)
//   - parses `Retry-After` on 429 and exposes it on the error
//   - exposes X-Request-Id on every response (and on errors) for
//     support requests

import { REQUEST_ID_HEADER, ERROR_CODES, type ErrorCode } from '@bumbum/shared';

// -----------------------------------------------------------------------------
// CSRF
// -----------------------------------------------------------------------------
// Backend expects `X-Requested-With: fetch` (a non-simple value) on
// every state-changing request. Browsers refuse to set this header
// cross-origin without CORS, so a successful preflight is itself the
// CSRF defense (the header just proves the request came from a JS
// context, not a form post). SameSite=Strict on the auth cookies is
// the cookie-side defense.
const CSRF_HEADER = 'X-Requested-With';

function isStateChanging(method: string): boolean {
  const m = method.toUpperCase();
  return m === 'POST' || m === 'PUT' || m === 'PATCH' || m === 'DELETE';
}

// -----------------------------------------------------------------------------
// Event: session-expired
// -----------------------------------------------------------------------------
// Dispatched when the silent refresh fails after a 401. Forms with
// unsaved state listen for this and show an interstitial instead of
// redirecting (phase-1-5 §9.3).
export const SESSION_EXPIRED_EVENT = 'bumbum:session-expired';

export interface SessionExpiredDetail {
  reason: 'refresh-failed' | 'unrecoverable-401';
  requestId: string | null;
}

function dispatchSessionExpired(detail: SessionExpiredDetail): void {
  if (typeof window === 'undefined') return;
  window.dispatchEvent(new CustomEvent<SessionExpiredDetail>(SESSION_EXPIRED_EVENT, { detail }));
}

// -----------------------------------------------------------------------------
// BroadcastChannel: cross-tab logout
// -----------------------------------------------------------------------------
// Per phase-1-5 §9.1. A logout / logout-all / forced revocation in
// one tab publishes to the channel; every other tab subscribes and
// re-fetches /me to discover the new auth state.
const AUTH_CHANNEL = 'bumbum:auth';
export const AUTH_EVENT_LOGOUT = 'logout';
export const AUTH_EVENT_LOGOUT_ALL = 'logout-all';
export const AUTH_EVENT_REAUTH = 'reauth';

let authChannel: BroadcastChannel | null = null;
function getAuthChannel(): BroadcastChannel | null {
  if (typeof window === 'undefined' || typeof BroadcastChannel === 'undefined') return null;
  if (!authChannel) authChannel = new BroadcastChannel(AUTH_CHANNEL);
  return authChannel;
}

export function publishAuthEvent(event: string): void {
  const ch = getAuthChannel();
  ch?.postMessage({ type: event, at: Date.now() });
}

// -----------------------------------------------------------------------------
// ApiError
// -----------------------------------------------------------------------------
export class ApiError extends Error {
  public readonly code: ErrorCode | 'NETWORK_ERROR';
  public readonly status: number;
  public readonly requestId: string | null;
  public readonly retryAfterSec: number | null;

  constructor(
    code: ApiError['code'],
    message: string,
    status: number,
    requestId: string | null,
    retryAfterSec: number | null = null,
  ) {
    super(message);
    this.name = 'ApiError';
    this.code = code;
    this.status = status;
    this.requestId = requestId;
    this.retryAfterSec = retryAfterSec;
  }

  /** Convenience for components that want to render different copy per code. */
  isOneOf(...codes: Array<ApiError['code']>): boolean {
    return codes.includes(this.code);
  }
}

// -----------------------------------------------------------------------------
// Error envelope
// -----------------------------------------------------------------------------
interface ErrorEnvelope {
  error?: { code?: string; message?: string; requestId?: string };
}

function isErrorEnvelope(value: unknown): value is ErrorEnvelope {
  return typeof value === 'object' && value !== null && 'error' in (value as Record<string, unknown>);
}

function parseRetryAfter(headerValue: string | null): number | null {
  if (!headerValue) return null;
  // Retry-After is either an integer (seconds) or an HTTP-date.
  const asNum = Number(headerValue);
  if (Number.isFinite(asNum) && asNum >= 0) return Math.ceil(asNum);
  const asDate = Date.parse(headerValue);
  if (Number.isNaN(asDate)) return null;
  return Math.max(0, Math.ceil((asDate - Date.now()) / 1000));
}

function buildApiError(status: number, body: unknown, requestId: string | null, retryAfter: number | null): ApiError {
  if (isErrorEnvelope(body) && body.error) {
    const rawCode = body.error.code;
    const code: ApiError['code'] =
      rawCode && (ERROR_CODES as Record<string, string>)[rawCode] === rawCode
        ? (rawCode as ErrorCode)
        : 'INTERNAL_ERROR';
    const message = body.error.message ?? `Request failed with status ${status}.`;
    return new ApiError(code, message, status, body.error.requestId ?? requestId, retryAfter);
  }
  return new ApiError(
    'NETWORK_ERROR',
    `Request failed with status ${status}.`,
    status,
    requestId,
    retryAfter,
  );
}

// -----------------------------------------------------------------------------
// Silent refresh — single in-flight promise shared across concurrent 401s
// -----------------------------------------------------------------------------
let refreshInFlight: Promise<boolean> | null = null;

async function tryRefresh(): Promise<boolean> {
  if (refreshInFlight) return refreshInFlight;
  refreshInFlight = (async () => {
    try {
      // Bare fetch — do NOT go through apiFetch (it would recurse on
      // a 401). The refresh endpoint sets new cookies on success.
      const res = await fetch('/api/v1/auth/refresh-token', {
        method: 'POST',
        credentials: 'include',
        headers: { [CSRF_HEADER]: 'fetch' },
      });
      return res.ok;
    } catch {
      return false;
    } finally {
      // Always clear, even on error, so the next 401 can retry
      // (after the user manually re-authenticates).
      refreshInFlight = null;
    }
  })();
  return refreshInFlight;
}

// -----------------------------------------------------------------------------
// apiFetch
// -----------------------------------------------------------------------------
export interface ApiRequestInit extends Omit<RequestInit, 'body' | 'headers'> {
  body?: unknown;
  headers?: HeadersInit | Record<string, string>;
  /**
   * If true, do not attempt silent refresh on 401 (used by the
   * /auth/refresh-token call itself to avoid recursion, and by
   * any caller that explicitly wants the 401 to bubble up).
   */
  skipRefresh?: boolean;
  /**
   * If true, do not surface 401 as `session-expired` (used for the
   * "am I signed in?" check that drives RequireAuth; a 401 there is
   * the normal "no" answer, not a session expiry).
   */
  suppressSessionExpired?: boolean;
}

export interface ApiResponse<T> {
  data: T;
  status: number;
  requestId: string;
}

export async function apiFetch<T>(path: string, init: ApiRequestInit = {}): Promise<ApiResponse<T>> {
  const { body, headers, skipRefresh, suppressSessionExpired, ...rest } = init;

  // Build headers. CSRF header on state-changing requests; Content-Type
  // only set when there's a body (don't set it on GETs — some endpoints
  // are picky about that).
  const finalHeaders: Record<string, string> = {
    ...(headers as Record<string, string> | undefined ?? {}),
  };
  const method = (rest.method ?? 'GET').toString().toUpperCase();
  if (isStateChanging(method) && finalHeaders[CSRF_HEADER] === undefined) {
    finalHeaders[CSRF_HEADER] = 'fetch';
  }
  if (body !== undefined && finalHeaders['Content-Type'] === undefined) {
    finalHeaders['Content-Type'] = 'application/json';
  }

  // Serialize body. Allow a string passthrough for the few callers
  // that want to send FormData, etc. (none today, but keeps the
  // escape hatch visible.)
  const fetchInit: RequestInit = {
    credentials: 'include',
    ...rest,
    method,
    headers: finalHeaders,
    body: body === undefined
      ? undefined
      : typeof body === 'string' ? body : JSON.stringify(body),
  };

  const doFetch = async () => fetch(path, fetchInit);
  let response = await doFetch();

  // Silent refresh on 401, exactly once.
  if (response.status === 401 && !skipRefresh) {
    const refreshed = await tryRefresh();
    if (refreshed) {
      response = await doFetch();
    } else if (!suppressSessionExpired) {
      dispatchSessionExpired({
        reason: 'refresh-failed',
        requestId: response.headers.get(REQUEST_ID_HEADER),
      });
    }
  }

  const requestId = response.headers.get(REQUEST_ID_HEADER);
  const retryAfter = parseRetryAfter(response.headers.get('Retry-After'));

  let parsedBody: unknown = null;
  const text = await response.text();
  if (text.length > 0) {
    try {
      parsedBody = JSON.parse(text);
    } catch {
      parsedBody = text;
    }
  }

  if (!response.ok) {
    throw buildApiError(response.status, parsedBody, requestId, retryAfter);
  }

  return {
    data: parsedBody as T,
    status: response.status,
    requestId: requestId ?? '',
  };
}

// -----------------------------------------------------------------------------
// apiGet / apiPost / apiPut / apiPatch / apiDelete — typed convenience
// -----------------------------------------------------------------------------
export const apiGet = <T>(path: string, init: ApiRequestInit = {}) =>
  apiFetch<T>(path, { ...init, method: 'GET' });

export const apiPost = <T>(path: string, body?: unknown, init: ApiRequestInit = {}) =>
  apiFetch<T>(path, { ...init, method: 'POST', body });

export const apiPut = <T>(path: string, body?: unknown, init: ApiRequestInit = {}) =>
  apiFetch<T>(path, { ...init, method: 'PUT', body });

export const apiPatch = <T>(path: string, body?: unknown, init: ApiRequestInit = {}) =>
  apiFetch<T>(path, { ...init, method: 'PATCH', body });

export const apiDelete = <T>(path: string, init: ApiRequestInit = {}) =>
  apiFetch<T>(path, { ...init, method: 'DELETE' });

// -----------------------------------------------------------------------------
// apiClient — object-shaped convenience client for the platform
// components (brands, creators, finance, etc.). Backed by apiFetch so
// every call inherits CSRF, silent-refresh and the session-expired
// behavior above.
// -----------------------------------------------------------------------------
export const apiClient = {
  get: <T = any>(path: string, init: ApiRequestInit = {}) => apiFetch<T>(path, { ...init, method: 'GET' }),
  post: <T = any>(path: string, body?: unknown, init: ApiRequestInit = {}) =>
    apiFetch<T>(path, { ...init, method: 'POST', body }),
  put: <T = any>(path: string, body?: unknown, init: ApiRequestInit = {}) =>
    apiFetch<T>(path, { ...init, method: 'PUT', body }),
  patch: <T = any>(path: string, body?: unknown, init: ApiRequestInit = {}) =>
    apiFetch<T>(path, { ...init, method: 'PATCH', body }),
  delete: <T = any>(path: string, init: ApiRequestInit = {}) =>
    apiFetch<T>(path, { ...init, method: 'DELETE' }),
};
